import { Button } from '@affine/component';
import { SeatAddonService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { type I18nString, useI18n } from '@affine/i18n';
import { CloseIcon, InformationFillDuotoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { type WorkspaceMetadata, WorkspacesService } from '../../workspace';
import { WorkspaceQuotaService } from '../services/quota';
import { openStoragePlans } from './open-storage-plans';
import { StorageFullDialog } from './storage-full-dialog';
import { storageFullMessage } from './storage-full-message';
import * as styles from './styles.css';

const readFlag = (key: string | null): boolean => {
  if (!key) {
    return false;
  }
  try {
    return globalThis.localStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, set: boolean) => {
  try {
    if (set) {
      globalThis.localStorage?.setItem(key, '1');
    } else {
      globalThis.localStorage?.removeItem(key);
    }
  } catch {
    // Blocked storage: the in-memory state still holds for this session.
  }
};

/** One storage-full dialog per workspace and full phase, kept over reloads. */
const storageFullDialogKey = (workspaceId: string) =>
  ['streamlain', 'quota', 'storage-full-dialog', workspaceId].join(':');

/**
 *
 * Notification that the cloud workspace quota has exceeded the limit
 *
 */
export const QuotaCheck = ({
  workspaceMeta,
}: {
  workspaceMeta: WorkspaceMetadata;
}) => {
  const workspacesService = useService(WorkspacesService);
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const workspaceQuota = workspaceQuotaService.quota;
  const seatAddonService = useService(SeatAddonService);
  const canPurchaseSeats = useLiveData(
    seatAddonService.seatAddon.canPurchaseSeats$
  );
  const workspaceProfile = workspacesService.getProfile(workspaceMeta);
  const quota = useLiveData(workspaceQuota.quota$);
  const usedPercent = useLiveData(workspaceQuota.percent$);
  const storageFull = useLiveData(workspaceQuotaService.storageFull$);
  const profile = useLiveData(workspaceProfile.profile$);
  const isOwner = profile?.isOwner;
  const isOwnerOrAdmin = !!profile?.isOwner || !!profile?.isAdmin;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const dialog = useLiveData(workspaceDialogService.dialogs$);
  const memberOverflow = (quota?.overcapacityMemberCount ?? 0) > 0;
  const memberOverageDismissKey =
    workspaceMeta.flavour === 'local' || !quota || !memberOverflow
      ? null
      : [
          'streamlain',
          'quota',
          'member-overage-banner',
          workspaceMeta.id,
          quota.memberCount,
          quota.memberLimit,
          quota.overcapacityMemberCount,
        ].join(':');
  const [dismissedMemberOverageKey, setDismissedMemberOverageKey] = useState<
    string | null
  >(() => (readFlag(memberOverageDismissKey) ? memberOverageDismissKey : null));
  const isMemberOverageDismissed =
    dismissedMemberOverageKey === memberOverageDismissKey ||
    readFlag(memberOverageDismissKey);

  const storageDialogKey = storageFullDialogKey(workspaceMeta.id);
  const [storageDialogDismissed, setStorageDialogDismissed] = useState(() =>
    readFlag(storageDialogKey)
  );

  useEffect(() => {
    if (isOwner) {
      seatAddonService.seatAddon.revalidate();
    }
  }, [isOwner, seatAddonService]);

  useEffect(() => {
    workspaceQuota?.revalidate();
  }, [workspaceQuota]);

  useEffect(() => {
    setDismissedMemberOverageKey(
      readFlag(memberOverageDismissKey) ? memberOverageDismissKey : null
    );
  }, [memberOverageDismissKey]);

  useEffect(() => {
    setStorageDialogDismissed(readFlag(storageDialogKey));
  }, [storageDialogKey]);

  // The full phase ends once the known quota has room and no doc waits for
  // storage; the next phase shows the dialog again.
  useEffect(() => {
    if (usedPercent !== null && !storageFull) {
      writeFlag(storageDialogKey, false);
      setStorageDialogDismissed(false);
    }
  }, [storageDialogKey, storageFull, usedPercent]);

  const onCloseStorageDialog = useCallback(() => {
    writeFlag(storageDialogKey, true);
    setStorageDialogDismissed(true);
  }, [storageDialogKey]);

  const onConfirmStorageDialog = useCallback(() => {
    onCloseStorageDialog();
    if (!isOwner) {
      return;
    }
    openStoragePlans(workspaceDialogService);
  }, [isOwner, onCloseStorageDialog, workspaceDialogService]);

  const onMemberOverageAction = useCallback(() => {
    if (!isOwnerOrAdmin) {
      return;
    }
    const activeTab = isOwner
      ? canPurchaseSeats
        ? 'workspace:billing'
        : 'workspace:plans'
      : 'workspace:members';
    if (
      dialog.some(d => d.type === 'setting' && d.props.activeTab === activeTab)
    ) {
      return;
    }
    workspaceDialogService.open('setting', {
      activeTab,
      scrollAnchor:
        isOwner && !canPurchaseSeats ? 'cloudPricingPlan' : undefined,
    });
  }, [
    canPurchaseSeats,
    dialog,
    isOwner,
    isOwnerOrAdmin,
    workspaceDialogService,
  ]);

  const onDismissMemberOverage = useCallback(() => {
    if (!memberOverageDismissKey) {
      return;
    }
    writeFlag(memberOverageDismissKey, true);
    setDismissedMemberOverageKey(memberOverageDismissKey);
  }, [memberOverageDismissKey]);

  if (workspaceMeta.flavour === 'local') {
    return null;
  }

  if (storageFull) {
    if (storageDialogDismissed || !profile) {
      return null;
    }
    return (
      <StorageFullDialog
        message={storageFullMessage(!!isOwner, memberOverflow)}
        onClose={onCloseStorageDialog}
        onConfirm={onConfirmStorageDialog}
      />
    );
  }

  if (memberOverflow && isOwnerOrAdmin && !isMemberOverageDismissed) {
    return (
      <MemberOverageBanner
        canPurchaseSeats={!!canPurchaseSeats}
        isOwner={!!isOwner}
        overcapacityMemberCount={quota?.overcapacityMemberCount ?? 0}
        onAction={onMemberOverageAction}
        onDismiss={onDismissMemberOverage}
      />
    );
  }

  return null;
};

const memberOverageKeys = {
  title: 'com.streamlain.payment.member-overage.banner.title' as I18nString,
  ownerDescription:
    'com.streamlain.payment.member-overage.banner.owner.description' as I18nString,
  adminDescription:
    'com.streamlain.payment.member-overage.banner.admin.description' as I18nString,
  ownerSeatsAction:
    'com.streamlain.payment.member-overage.banner.owner.seats-action' as I18nString,
  ownerPlanAction:
    'com.streamlain.payment.member-overage.banner.owner.plan-action' as I18nString,
  adminAction:
    'com.streamlain.payment.member-overage.banner.admin.action' as I18nString,
  dismiss: 'com.streamlain.payment.member-overage.banner.dismiss' as I18nString,
};

const MemberOverageBanner = ({
  canPurchaseSeats,
  isOwner,
  overcapacityMemberCount,
  onAction,
  onDismiss,
}: {
  canPurchaseSeats: boolean;
  isOwner: boolean;
  overcapacityMemberCount: number;
  onAction: () => void;
  onDismiss: () => void;
}) => {
  const t = useI18n();
  return (
    <div className={styles.memberOverageBanner} role="status">
      <InformationFillDuotoneIcon className={styles.memberOverageIcon} />
      <div className={styles.memberOverageContent}>
        <div className={styles.memberOverageTitle}>
          {t.t(memberOverageKeys.title)}
        </div>
        <div className={styles.memberOverageDescription}>
          {t.t(
            isOwner
              ? memberOverageKeys.ownerDescription
              : memberOverageKeys.adminDescription,
            { count: String(overcapacityMemberCount) }
          )}
        </div>
      </div>
      <Button variant="primary" onClick={onAction}>
        {t.t(
          isOwner
            ? canPurchaseSeats
              ? memberOverageKeys.ownerSeatsAction
              : memberOverageKeys.ownerPlanAction
            : memberOverageKeys.adminAction
        )}
      </Button>
      <button
        type="button"
        className={styles.memberOverageDismiss}
        aria-label={t.t(memberOverageKeys.dismiss)}
        onClick={onDismiss}
      >
        <CloseIcon />
      </button>
    </div>
  );
};
